/**
 * Express DB Adapter — Cliente PostgREST-style sobre /api/db/*
 * Sprint 5/6 EPIC-MG-001
 *
 * Implementa a mesma API que o supabase client expõe (db.from(...).select()...
 * .eq().neq()...) mas faz fetch para os endpoints Express genéricos
 * (/api/db/select, /insert, /update, /delete, /rpc/:fn).
 *
 * Adapter único desde 2026-09-14 (o do Supabase foi removido) —
 * mexer em nenhum hook do frontend.
 */

import type { DbClient, DbResult } from './types';
import { handleUnauthorized } from '@/lib/api/session-guard';

const API_BASE = import.meta.env?.VITE_API_URL || 'http://localhost:3001';
const TOKEN_KEY = 'auth_token';

interface Filter {
  type: string;
  col: string;
  val: any;
  operator?: string;
}

function getAuthHeaders(): Record<string, string> {
  const token = localStorage.getItem(TOKEN_KEY);
  const h: Record<string, string> = { 'Content-Type': 'application/json' };
  if (token) h['Authorization'] = `Bearer ${token}`;
  return h;
}

async function postJson<T>(path: string, body: any): Promise<DbResult<T>> {
  try {
    const res = await fetch(`${API_BASE}${path}`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(body),
    });

    // Defensive parse: response.json() lança SyntaxError "Unexpected token"
    // quando o body não é JSON (ex: HTML 502/504 do nginx, redirect de auth,
    // body parser falhando intermitentemente). Walter relatou esse erro em
    // salvar layout (reunião 2026-05-06, item 2). Convertemos em erro
    // estruturado com Content-Type + status para diagnóstico em vez de
    // propagar o crash.
    const text = await res.text();
    let json: any = null;
    try {
      json = text ? JSON.parse(text) : null;
    } catch {
      const ct = res.headers.get('content-type') || 'unknown';
      const preview = text.slice(0, 120).replace(/\s+/g, ' ');
      console.error(
        `[express-adapter] resposta não-JSON em ${path}: status=${res.status} content-type=${ct} body[0..120]="${preview}"`,
      );
      return {
        data: null,
        error: {
          message: `Servidor retornou resposta inválida (HTTP ${res.status}). Tente novamente — se persistir, contate suporte.`,
          details: `status=${res.status} content-type=${ct} body="${preview}"`,
        },
      };
    }
    // Sessão morta: encerra e vai para o login. Sem isto o erro virava
    // `data: null`, os hooks liam como "sem dados" e o quadro mostrava ZEROS
    // — parecendo uma fábrica parada em vez de uma sessão vencida.
    if (res.status === 401 && handleUnauthorized(path)) {
      return { data: null, error: { message: 'Sessão expirada. Entre novamente.' } };
    }
    if (!res.ok) {
      return {
        data: null,
        error: { message: json?.error?.message || `HTTP ${res.status}`, details: json?.error?.details, code: json?.error?.code },
      };
    }
    return json;
  } catch (e: any) {
    return { data: null, error: { message: e?.message || 'Network error' } };
  }
}

// ─── Query Builder ────────────────────────────────────────────────────

class SelectBuilder {
  private filters: Filter[] = [];
  private orFilters?: string;
  private orderBy?: { col: string; ascending?: boolean; nullsFirst?: boolean };
  private limitN?: number;
  private rangeFromTo?: { from: number; to: number };
  private countMode?: 'exact' | 'planned' | 'estimated';
  private headOnly?: boolean;

  constructor(private table: string, private columns: string = '*', options?: { count?: 'exact' | 'planned' | 'estimated'; head?: boolean }) {
    if (options?.count) this.countMode = options.count;
    if (options?.head) this.headOnly = options.head;
  }

  eq(col: string, val: any) { this.filters.push({ type: 'eq', col, val }); return this; }
  neq(col: string, val: any) { this.filters.push({ type: 'neq', col, val }); return this; }
  gt(col: string, val: any) { this.filters.push({ type: 'gt', col, val }); return this; }
  gte(col: string, val: any) { this.filters.push({ type: 'gte', col, val }); return this; }
  lt(col: string, val: any) { this.filters.push({ type: 'lt', col, val }); return this; }
  lte(col: string, val: any) { this.filters.push({ type: 'lte', col, val }); return this; }
  is(col: string, val: any) { this.filters.push({ type: 'is', col, val }); return this; }
  in(col: string, vals: any[]) { this.filters.push({ type: 'in', col, val: vals }); return this; }
  ilike(col: string, pattern: string) { this.filters.push({ type: 'ilike', col, val: pattern }); return this; }
  like(col: string, pattern: string) { this.filters.push({ type: 'like', col, val: pattern }); return this; }
  contains(col: string, val: any) { this.filters.push({ type: 'contains', col, val }); return this; }
  not(col: string, op: string, val: any) { this.filters.push({ type: 'not', col, val, operator: op }); return this; }
  or(filters: string, _options?: { foreignTable?: string }) { this.orFilters = filters; return this; }
  match(query: Record<string, any>) {
    for (const [k, v] of Object.entries(query)) this.filters.push({ type: 'eq', col: k, val: v });
    return this;
  }

  order(col: string, options?: { ascending?: boolean; nullsFirst?: boolean }) {
    // Defensive: caller passing undefined/null/empty causes backend split crash
    // (db.ts:308 quoteIdent(undefined)). Capturado via Walter 2026-05-06 — busca
    // arquivados não funcionava porque toda query arte_jobs crashava silenciosamente.
    // Backend agora tolera (db.ts), mas logamos aqui pra identificar o caller.
    if (typeof col !== 'string' || col.length === 0) {
      console.warn(
        `[express-adapter.order] col inválido em ${this.table} — ORDER BY pulado.`,
        { col, options, stack: new Error().stack },
      );
      return this; // não setar orderBy se col inválido
    }
    this.orderBy = { col, ascending: options?.ascending !== false, nullsFirst: options?.nullsFirst };
    return this;
  }
  limit(n: number) { this.limitN = n; return this; }
  range(from: number, to: number) { this.rangeFromTo = { from, to }; return this; }

  private payload(extra: { single?: boolean; maybeSingle?: boolean } = {}) {
    return {
      table: this.table,
      columns: this.columns,
      filters: this.filters,
      orFilters: this.orFilters,
      order: this.orderBy,
      limit: this.limitN,
      range: this.rangeFromTo,
      count: this.countMode,
      head: this.headOnly,
      ...extra,
    };
  }

  single() { return postJson('/api/db/select', this.payload({ single: true })); }
  maybeSingle() { return postJson('/api/db/select', this.payload({ maybeSingle: true })); }
  then<TResult1 = any, TResult2 = never>(
    onfulfilled?: ((value: DbResult<any>) => TResult1 | PromiseLike<TResult1>) | null,
    onrejected?: ((reason: any) => TResult2 | PromiseLike<TResult2>) | null
  ) {
    return postJson('/api/db/select', this.payload()).then(onfulfilled, onrejected);
  }
}

class InsertBuilder {
  private wantsSelect = false;
  private returning = '*';

  constructor(private table: string, private values: any | any[]) {}

  select(columns: string = '*') {
    this.wantsSelect = true;
    this.returning = columns;
    return this; // for chaining .single()
  }

  single(): Promise<DbResult<any>> {
    return postJson('/api/db/insert', { table: this.table, values: this.values, returning: this.returning })
      .then(r => ({ ...r, data: Array.isArray(r.data) ? (r.data[0] ?? null) : r.data }));
  }

  then<TResult1 = any, TResult2 = never>(
    onfulfilled?: ((value: DbResult<any>) => TResult1 | PromiseLike<TResult1>) | null,
    onrejected?: ((reason: any) => TResult2 | PromiseLike<TResult2>) | null
  ) {
    return postJson('/api/db/insert', {
      table: this.table,
      values: this.values,
      returning: this.wantsSelect ? this.returning : undefined,
    }).then(onfulfilled, onrejected);
  }
}

class UpdateDeleteBuilder {
  private filters: Filter[] = [];
  private returning?: string;

  constructor(private op: 'update' | 'delete', private table: string, private values?: any) {}

  eq(col: string, val: any) { this.filters.push({ type: 'eq', col, val }); return this; }
  neq(col: string, val: any) { this.filters.push({ type: 'neq', col, val }); return this; }
  is(col: string, val: any) { this.filters.push({ type: 'is', col, val }); return this; }
  in(col: string, vals: any[]) { this.filters.push({ type: 'in', col, val: vals }); return this; }
  not(col: string, op: string, val: any) { this.filters.push({ type: 'not', col, val, operator: op }); return this; }
  match(query: Record<string, any>) {
    for (const [k, v] of Object.entries(query)) this.filters.push({ type: 'eq', col: k, val: v });
    return this;
  }

  select(columns: string = '*') { this.returning = columns; return this; }
  single(): Promise<DbResult<any>> {
    const path = this.op === 'update' ? '/api/db/update' : '/api/db/delete';
    return postJson(path, {
      table: this.table,
      values: this.values,
      filters: this.filters,
      returning: this.returning || '*',
    }).then(r => ({ ...r, data: Array.isArray(r.data) ? (r.data[0] ?? null) : r.data }));
  }

  then<TResult1 = any, TResult2 = never>(
    onfulfilled?: ((value: DbResult<any>) => TResult1 | PromiseLike<TResult1>) | null,
    onrejected?: ((reason: any) => TResult2 | PromiseLike<TResult2>) | null
  ) {
    const path = this.op === 'update' ? '/api/db/update' : '/api/db/delete';
    return postJson(path, {
      table: this.table,
      values: this.values,
      filters: this.filters,
      returning: this.returning,
    }).then(onfulfilled, onrejected);
  }
}

// ─── Adapter ──────────────────────────────────────────────────────────

class ExpressDbAdapter implements DbClient {
  from(table: string): any {
    return {
      select: (columns?: string, options?: { count?: 'exact' | 'planned' | 'estimated'; head?: boolean }) =>
        new SelectBuilder(table, columns, options),
      insert: (data: any) => new InsertBuilder(table, data),
      update: (data: any) => new UpdateDeleteBuilder('update', table, data),
      delete: () => new UpdateDeleteBuilder('delete', table),
      upsert: (data: any) => new InsertBuilder(table, data), // simplified; real upsert seria implementado depois
    };
  }

  /**
   * Refactor 2026-05-04: tenta primeiro a rota Express dedicada
   * `/api/rpc/${fn}` (que replica a lógica original das RPCs Postgres do
   * Supabase) e, em caso de 404 / função não migrada, faz fallback para
   * `/api/db/rpc/${fn}` (que tenta a função no Postgres e retorna empty
   * graceful se não existir).
   *
   * As rotas dedicadas em server/src/routes/rpc.ts cobrem:
   *   - get_expedicao_dates
   *   - get_chat_contacts
   *   - get_distinct_labels
   *   - set_active_sector
   *   - update_arte_job_link
   *
   * Antes do fix, todas as RPCs caíam silenciosamente no `/api/db/rpc/:fn`
   * sem persistir nada (toast verde mas zero efeito).
   */
  async rpc<T = any>(fn: string, params?: Record<string, any>): Promise<DbResult<T[]>> {
    // 2026-10-08 (auditoria B09, passo 1): a projeção dos itens é função do
    // Postgres servida SÓ pela porta genérica — ir primeiro à dedicada custava
    // um 404 e um console.error a cada Programar, para nada.
    if (SO_NA_PORTA_GENERICA.has(fn)) {
      return postJson<T[]>(`/api/db/rpc/${fn}`, params || {});
    }
    const dedicated = await postJson<T[]>(`/api/rpc/${fn}`, params || {});
    // 404 explícito → cai pro genérico (cobre RPCs ainda não migradas)
    const is404 =
      dedicated.error?.message?.includes('404') ||
      dedicated.error?.message === 'Cannot POST /api/rpc/' + fn;
    if (is404) {
      return postJson<T[]>(`/api/db/rpc/${fn}`, params || {});
    }
    return dedicated;
  }
}

/** RPCs que não têm rota dedicada em server/src/routes/rpc.ts (ver RPC_ALLOWLIST em routes/db.ts). */
const SO_NA_PORTA_GENERICA = new Set(['project_order_metadata_items']);

export const expressDbAdapter = new ExpressDbAdapter();
